///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///

import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { validatePlacemark } from "./validation/placemarksValidation"

/**
 * Preset accent palette for placemark pins. Placemarks store the key;
 * the hex values drive both the globe marker tint and UI swatches.
 */
export const cPlacemarkColors: Record<string, string> = {
	red: "#e5484d",
	orange: "#f76b15",
	amber: "#ffb224",
	green: "#46a758",
	teal: "#12a594",
	blue: "#0091ff",
	purple: "#8e4ec6",
	pink: "#d6409f",
}

export const cDefaultPlacemarkColor = "red"

/** Hard client-side cap on locations per placemark (tier limits enforce less server-side) */
export const cMaxPlacemarkLocations = 500

/**
 * One member location of a placemark. A placemark is a named *place*
 * backed by 1..N locations: a single dropped pin is the N=1 case, a CSV
 * of sites is the N=many case, and both behave as one unit (one name,
 * one color, one sync record, one share URL, one alert rule).
 */
export interface PlacemarkLocation {
	/** Stable within the placemark (member deep links, alert reports) */
	id: string
	/** Degrees */
	lat: number
	/** Degrees */
	lon: number
	/** Member label (e.g. CSV name column) */
	name?: string
	/** Ground-clamped when absent */
	altitudeM?: number
	/** Short per-member free text */
	note?: string
}

export interface Placemark {
	/** uuid, generated client-side so create/update is one upsert verb */
	id: string
	name: string
	/** 1..cMaxPlacemarkLocations; [0] is the anchor unless anchorId is set */
	locations: PlacemarkLocation[]
	/** User-designated primary member (anchor pin + conditions source) */
	anchorId?: string
	/** Key into cPlacemarkColors */
	color: string
	/** Short free text */
	note?: string
	tags: string[]
	createdAt: string
	/** ISO date, drives sync conflict resolution (last-write-wins, whole-placemark) */
	updatedAt: string
}

/** The location the anchor pin renders at (and conditions sample from) */
export function placemarkAnchor(placemark: Placemark): PlacemarkLocation {
	if (placemark.anchorId) {
		const anchor = placemark.locations.find(location => location.id === placemark.anchorId)
		if (anchor) return anchor
	}
	return placemark.locations[0]
}

export interface PlacemarksState {
	placemarks: Record<string, Placemark>
	/** Tombstones so deletions can propagate across devices (sync milestone) */
	deleted: Record<string, string>
	lastSyncedAt: string | null
}

const initialState: PlacemarksState = {
	placemarks: {},
	deleted: {},
	lastSyncedAt: null,
}

export const placemarksSlice = createSlice({
	name: "placemarks",
	initialState,
	reducers: {
		upsertPlacemark: (state, action: PayloadAction<Placemark>) => {
			const placemark = validatePlacemark(action.payload)
			if (!placemark) return
			state.placemarks[placemark.id] = placemark
			// Re-creating a previously deleted id clears its tombstone
			delete state.deleted[placemark.id]
		},
		editPlacemark: {
			reducer: (state, action: PayloadAction<{
				id: string
				name?: string
				color?: string
				note?: string | null
				anchorId?: string | null
				tags?: string[]
				updatedAt: string
			}>) => {
				const { id, name, color, note, anchorId, tags, updatedAt } = action.payload
				const placemark = state.placemarks[id]
				if (!placemark) return

				if (typeof name === "string" && name.trim().length > 0) {
					placemark.name = name.trim()
				}
				if (typeof color === "string" && cPlacemarkColors[color]) {
					placemark.color = color
				}
				if (note !== undefined) {
					placemark.note = typeof note === "string" && note.length > 0 ? note : undefined
				}
				if (anchorId !== undefined) {
					placemark.anchorId = anchorId !== null &&
						placemark.locations.some(location => location.id === anchorId)
						? anchorId : undefined
				}
				if (Array.isArray(tags)) {
					placemark.tags = tags.filter(tag => typeof tag === "string" && tag.length > 0)
				}
				placemark.updatedAt = updatedAt
			},
			prepare: (payload: {
				id: string
				name?: string
				color?: string
				note?: string | null
				anchorId?: string | null
				tags?: string[]
			}) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		addLocations: {
			reducer: (state, action: PayloadAction<{
				id: string
				locations: PlacemarkLocation[]
				updatedAt: string
			}>) => {
				const { id, locations, updatedAt } = action.payload
				const placemark = state.placemarks[id]
				if (!placemark) return

				const existingIds = new Set(placemark.locations.map(location => location.id))
				const merged = [...placemark.locations]
				for (const location of locations) {
					if (merged.length >= cMaxPlacemarkLocations) break
					if (existingIds.has(location.id)) continue
					merged.push(location)
					existingIds.add(location.id)
				}
				// Re-validate the whole placemark so malformed members are dropped
				const validated = validatePlacemark({ ...placemark, locations: merged, updatedAt })
				if (!validated) return
				state.placemarks[id] = validated
			},
			prepare: (payload: { id: string; locations: PlacemarkLocation[] }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		editLocation: {
			reducer: (state, action: PayloadAction<{
				id: string
				locationId: string
				name?: string
				note?: string | null
				updatedAt: string
			}>) => {
				const { id, locationId, name, note, updatedAt } = action.payload
				const placemark = state.placemarks[id]
				if (!placemark) return
				const location = placemark.locations.find(l => l.id === locationId)
				if (!location) return

				if (typeof name === "string" && name.trim().length > 0) {
					location.name = name.trim()
				}
				if (note !== undefined) {
					location.note = typeof note === "string" && note.trim().length > 0
						? note.trim() : undefined
				}
				placemark.updatedAt = updatedAt
			},
			prepare: (payload: {
				id: string
				locationId: string
				name?: string
				note?: string | null
			}) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		removeLocation: {
			reducer: (state, action: PayloadAction<{
				id: string
				locationId: string
				updatedAt: string
			}>) => {
				const { id, locationId, updatedAt } = action.payload
				const placemark = state.placemarks[id]
				if (!placemark) return

				const remaining = placemark.locations.filter(location => location.id !== locationId)
				// A placemark always has at least one location; removing the
				// last member is a placemark deletion, not a member edit
				if (remaining.length === 0 || remaining.length === placemark.locations.length) return
				placemark.locations = remaining
				if (placemark.anchorId === locationId) {
					placemark.anchorId = undefined
				}
				placemark.updatedAt = updatedAt
			},
			prepare: (payload: { id: string; locationId: string }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		deletePlacemark: {
			reducer: (state, action: PayloadAction<{ id: string; deletedAt: string }>) => {
				const { id, deletedAt } = action.payload
				if (!state.placemarks[id]) return
				delete state.placemarks[id]
				state.deleted[id] = deletedAt
			},
			prepare: (id: string) => ({
				payload: { id, deletedAt: new Date().toISOString() },
			}),
		},
		// Applied by launch validation to repair a corrupted persisted slice
		applyValidatedPlacemarksState: (_state, action: PayloadAction<PlacemarksState>) => {
			return action.payload
		},

		// ========== Sync (server merge) ==========

		/**
		 * Apply a server delta with per-placemark last-write-wins. Local
		 * edits newer than the server copy survive (the orchestrator pushes
		 * them up); server tombstones remove local placemarks unless the
		 * local edit is newer than the deletion.
		 */
		mergeServerPlacemarks: (state, action: PayloadAction<{
			placemarks: Placemark[]
			tombstones: Array<{ id: string; deletedAt: string }>
		}>) => {
			for (const incoming of action.payload.placemarks) {
				const placemark = validatePlacemark(incoming)
				if (!placemark) continue

				// A local deletion newer than the server copy wins (the
				// orchestrator pushes the delete up)
				const localDeletedAt = state.deleted[placemark.id]
				if (localDeletedAt && localDeletedAt >= placemark.updatedAt) continue

				const local = state.placemarks[placemark.id]
				if (!local || placemark.updatedAt > local.updatedAt) {
					state.placemarks[placemark.id] = placemark
					delete state.deleted[placemark.id]
				}
			}

			for (const tombstone of action.payload.tombstones) {
				const local = state.placemarks[tombstone.id]
				// A local edit newer than the deletion wins (gets pushed up)
				if (local && local.updatedAt > tombstone.deletedAt) continue
				delete state.placemarks[tombstone.id]
				// The server already owns this tombstone; no need to push it
				delete state.deleted[tombstone.id]
			}
		},

		/** Advance the sync cursor (only after all pushes succeeded) */
		markPlacemarksSynced: (state, action: PayloadAction<string>) => {
			state.lastSyncedAt = action.payload
		},

		/** Drop tombstones whose deletion reached the server */
		removePlacemarkTombstones: (state, action: PayloadAction<string[]>) => {
			for (const id of action.payload) {
				delete state.deleted[id]
			}
		},
	},
})

export const {
	upsertPlacemark,
	editPlacemark,
	addLocations,
	editLocation,
	removeLocation,
	deletePlacemark,
	applyValidatedPlacemarksState,
	mergeServerPlacemarks,
	markPlacemarksSynced,
	removePlacemarkTombstones,
} = placemarksSlice.actions

export default placemarksSlice.reducer
